"use client"

import { useCallback, useState, useRef } from "react"
import { toast } from "sonner"

export interface UploadedFile {
  id: string
  file: File
  preview: string
  name: string
  size: number
  width?: number
  height?: number
}

interface UseFileUploadOptions {
  accept?: string[]
  maxFileSize?: number
  maxTotalSize?: number
  maxFiles?: number
  onFilesAdded?: (files: UploadedFile[]) => void
}

const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024
const DEFAULT_MAX_TOTAL_SIZE = 250 * 1024 * 1024

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const {
    accept = ["image/*"],
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    maxTotalSize = DEFAULT_MAX_TOTAL_SIZE,
    maxFiles,
    onFilesAdded,
  } = options

  const [files, setFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const totalSize = files.reduce((sum, f) => sum + f.size, 0)

  const generateId = () => Math.random().toString(36).substring(2, 11)

  const loadImageDimensions = (file: File, preview: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve({ width: 0, height: 0 })
        return
      }
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => resolve({ width: 0, height: 0 })
      img.src = preview
    })
  }

  const processFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const filesToProcess = Array.from(fileList)
      const validFiles: UploadedFile[] = []
      let currentTotalSize = totalSize

      for (const file of filesToProcess) {
        const acceptPatterns = accept.map((a) => {
          if (a.endsWith("/*")) {
            return (type: string) => type.startsWith(a.replace("/*", "/"))
          }
          return (type: string) => type === a
        })

        const isAccepted = acceptPatterns.some((pattern) => pattern(file.type))
        if (!isAccepted) {
          toast.error(`${file.name} is not an accepted file type`)
          continue
        }

        if (file.size > maxFileSize) {
          toast.error(`${file.name} exceeds ${formatBytes(maxFileSize)} limit`)
          continue
        }

        if (currentTotalSize + file.size > maxTotalSize) {
          toast.error(`Total file size would exceed ${formatBytes(maxTotalSize)} limit`)
          continue
        }

        if (maxFiles && files.length + validFiles.length >= maxFiles) {
          toast.error(`Maximum ${maxFiles} files allowed`)
          break
        }

        const preview = URL.createObjectURL(file)
        const dimensions = await loadImageDimensions(file, preview)

        validFiles.push({
          id: generateId(),
          file,
          preview,
          name: file.name,
          size: file.size,
          ...dimensions,
        })

        currentTotalSize += file.size
      }

      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles])
        onFilesAdded?.(validFiles)
      }

      if (inputRef.current) {
        inputRef.current.value = ""
      }
    },
    [accept, maxFileSize, maxTotalSize, maxFiles, files.length, totalSize, onFilesAdded]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(e.target.files)
      }
    },
    [processFiles]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      if (e.dataTransfer.files) {
        processFiles(e.dataTransfer.files)
      }
    },
    [processFiles]
  )

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id)
      if (file) {
        URL.revokeObjectURL(file.preview)
      }
      return prev.filter((f) => f.id !== id)
    })
  }, [])

  const clearFiles = useCallback(() => {
    files.forEach((f) => URL.revokeObjectURL(f.preview))
    setFiles([])
  }, [files])

  const updateFile = useCallback((id: string, updates: Partial<UploadedFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)))
  }, [])

  const reorderFiles = useCallback((fromIndex: number, toIndex: number) => {
    setFiles((prev) => {
      const result = [...prev]
      const [removed] = result.splice(fromIndex, 1)
      result.splice(toIndex, 0, removed)
      return result
    })
  }, [])

  const openFilePicker = useCallback(() => {
    inputRef.current?.click()
  }, [])

  return {
    files,
    setFiles,
    totalSize,
    isDragging,
    inputRef,
    inputProps: {
      ref: inputRef,
      type: "file" as const,
      accept: accept.join(","),
      multiple: maxFiles !== 1,
      onChange: handleInputChange,
      className: "hidden",
    },
    dropZoneProps: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    removeFile,
    clearFiles,
    updateFile,
    reorderFiles,
    openFilePicker,
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
